// pages/Users/AddUser.js - Complete with Staff and Customer fields only

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaArrowLeft, FaUserPlus, FaStore, FaBuilding, FaMapMarkerAlt,
  FaUserTag, FaLock, FaSpinner, FaAddressCard, FaCheckCircle,
  FaPhone, FaEnvelope, FaIdCard, FaPlus, FaTimes, FaKeyboard,
  FaUser, FaUsers, FaUserSlash, FaShieldAlt, FaReceipt, FaTruck
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosConfig';
import './users.css';

const AddUser = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get shop ID from URL path (e.g., /shop/123/add-user)
  const pathMatch = location.pathname.match(/\/shop\/(\d+)\/add-user/);
  const shopIdFromPath = pathMatch ? pathMatch[1] : null;

  // Also check query params
  const urlParams = new URLSearchParams(location.search);
  const shopIdFromQuery = urlParams.get('shop_id');

  // Optional preset user type, e.g. coming from the Customers page's
  // "Add Customer" button (/shop/123/add-user?type=CUSTOMER)
  const typeFromQuery = urlParams.get('type');

  // Check where the user came from (for redirect after save)
  const returnToFromQuery = urlParams.get('returnTo');

  // Get from location state
  const shopIdFromState = location.state?.defaultShop || null;
  const returnToFromState = location.state?.returnTo || null;

  // Final shop ID - priority: path > query > state
  const initialShopId = shopIdFromPath || shopIdFromQuery || shopIdFromState || '';

  // Final return destination - priority: state > query
  const returnTo = returnToFromState || returnToFromQuery || null;

  const [loading, setLoading] = useState(false);
  const [shops, setShops] = useState([]);
  const [companyAddresses, setCompanyAddresses] = useState([]);
  const [loadingShops, setLoadingShops] = useState(true);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  // City selection states
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [cityFetchError, setCityFetchError] = useState(null);

  // ✅ Address form state (replaces manual/dropdown toggle)
  const [userAddress, setUserAddress] = useState({
    house_no: '',
    street: '',
    area: '',
    postcode: ''
  });

  // ✅ Only CUSTOMER and STAFF are allowed to be created now.
  const validUserTypes = ['CUSTOMER', 'STAFF'];
  const presetType = validUserTypes.includes((typeFromQuery || '').toUpperCase())
    ? typeFromQuery.toUpperCase()
    : 'STAFF';

  const [formData, setFormData] = useState({
    password: '',
    type: presetType,
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    companySize: '',
    vatNumber: '',
    isActive: true,
    selected_city: '',
    applyVat: false,
    applyDeliveryCharges: false
  });

  const [selectedShop, setSelectedShop] = useState(initialShopId);
  const [selectedCompanyAddress, setSelectedCompanyAddress] = useState('');

  const userTypes = [
    { value: 'STAFF', label: 'Staff' },
    { value: 'CUSTOMER', label: 'Customer' }
  ];

  const isCustomerType = formData.type === 'CUSTOMER' || formData.type === 'INACTIVE_CUSTOMER';

  // Company size options
  const companySizes = [
    { value: '', label: 'Select company size' },
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '500+', label: '500+ employees' }
  ];

  // Fetch shops on component mount
  useEffect(() => {
    fetchShops();
  }, []);

  // Fetch company addresses and cities when shop changes
  useEffect(() => {
    if (selectedShop) {
      fetchCompanyAddresses();
      fetchShopCities(selectedShop);
    } else {
      setCompanyAddresses([]);
      setSelectedCompanyAddress('');
      setCities([]);
      setFormData(prev => ({ ...prev, selected_city: '' }));
      setCityFetchError(null);
    }
  }, [selectedShop]);

  // If initialShopId is provided, fetch cities for it
  useEffect(() => {
    if (initialShopId) {
      fetchShopCities(initialShopId);
    }
  }, [initialShopId]);

  const fetchShops = async () => {
    try {
      setLoadingShops(true);
      const response = await axiosInstance.get('/user/shops/');
      setShops(response.data);
    } catch (err) {
      console.error('Error fetching shops:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load shops',
        timer: 3000,
        showConfirmButton: true
      });
    } finally {
      setLoadingShops(false);
    }
  };

  const fetchCompanyAddresses = async () => {
    if (!selectedShop) {
      setCompanyAddresses([]);
      return;
    }

    try {
      setLoadingAddresses(true);
      const url = `/user/company-addresses/?shop_id=${selectedShop}`;
      const response = await axiosInstance.get(url);
      console.log('Fetched company addresses:', response.data);
      setCompanyAddresses(response.data);
    } catch (err) {
      console.error('Error fetching company addresses:', err);
      setCompanyAddresses([]);
    } finally {
      setLoadingAddresses(false);
    }
  };

  // Fetch cities for selected shop
  const fetchShopCities = async (shopId) => {
    if (!shopId) {
      setCities([]);
      setFormData(prev => ({ ...prev, selected_city: '' }));
      setCityFetchError(null);
      return;
    }

    try {
      setLoadingCities(true);
      setCityFetchError(null);

      console.log(`🔄 Fetching cities for shop ID: ${shopId}`);

      const response = await axiosInstance.get(`/user/shop-cities/?shop_id=${shopId}`);
      console.log('📊 Shop cities response:', response.data);

      let citiesData = [];

      if (response.data) {
        if (Array.isArray(response.data.cities)) {
          citiesData = response.data.cities;
        } else if (Array.isArray(response.data)) {
          citiesData = response.data;
        } else if (response.data.cities && typeof response.data.cities === 'string') {
          try {
            citiesData = JSON.parse(response.data.cities);
          } catch {
            citiesData = response.data.cities.split(',').map(c => c.trim()).filter(c => c);
          }
        } else {
          console.warn('⚠️ Unexpected cities format:', response.data);
          citiesData = [];
        }
      }

      console.log(`✅ Setting cities: ${citiesData.length} cities found`, citiesData);
      setCities(citiesData);

      if (formData.selected_city && !citiesData.includes(formData.selected_city)) {
        setFormData(prev => ({ ...prev, selected_city: '' }));
      }

    } catch (err) {
      console.error('❌ Error fetching shop cities:', err);
      setCityFetchError('Failed to load cities for this shop');
      setCities([]);

      if (err.response?.status === 401) {
        await Swal.fire({
          icon: 'error',
          title: 'Session Expired',
          text: 'Please login again',
          timer: 3000,
          showConfirmButton: true
        });
        navigate('/signin');
      }
    } finally {
      setLoadingCities(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // ✅ Handle user address form changes
  const handleUserAddressChange = (e) => {
    const { name, value } = e.target;
    setUserAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleShopChange = (e) => {
    const newShopId = e.target.value;
    setSelectedShop(newShopId);
    setSelectedCompanyAddress('');

    setFormData(prev => ({ ...prev, selected_city: '' }));
    setCityFetchError(null);
    setCities([]);
  };

  // ✅ Build the full address string from the address fields
  const buildFullAddress = () => {
    const parts = [
      userAddress.house_no.trim(),
      userAddress.street.trim(),
      userAddress.area.trim(),
      userAddress.postcode.trim()
    ].filter(Boolean);
    return parts.join(', ');
  };

  const validateForm = () => {
    if (!formData.email) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Email is required',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (!formData.password) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Password is required',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (formData.password.length < 8) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Password must be at least 8 characters long',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (!formData.firstName) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'First name is required',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (!formData.lastName) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Last name is required',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (!selectedShop) {
      Swal.fire({
        icon: 'warning',
        title: 'Shop Required',
        text: 'Please select a shop for this user',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (formData.type === 'STAFF') {
      if (selectedShop && cities.length > 0 && !formData.selected_city) {
        Swal.fire({
          icon: 'warning',
          title: 'City Required',
          text: 'Please select a city for staff user',
          timer: 2000,
          showConfirmButton: true
        });
        return false;
      }
    }

    // ✅ NAYA VALIDATION: Address ki length check karein (Server error se bachne ke liye)
    const fullAddress = buildFullAddress();
    if (fullAddress.length > 250) {
      Swal.fire({
        icon: 'warning',
        title: 'Address Too Long',
        text: 'The address is too long. Please shorten the House No, Street, Area or Postcode.',
        timer: 3000,
        showConfirmButton: true
      });
      return false;
    }

    return true;
  };

  // ✅ FIXED: Build user data with address object (not string)
  const buildUserData = () => {
    // Build address object for proper saving
    const addressObject = {
      city: formData.selected_city || '',
      area: userAddress.area || '',
      street_Number: userAddress.street || '',
      house_Number: userAddress.house_no || '',
      postal_code: userAddress.postcode || ''
    };

    return {
      first_Name: formData.firstName,
      last_Name: formData.lastName,
      email: formData.email,
      phone_Number: formData.phoneNumber,
      password: formData.password,
      type: formData.type.toUpperCase(),
      shop_id: selectedShop || null,
      address: addressObject,  // ✅ Send as object for proper saving
      company_name: formData.companyName,
      company_size: formData.companySize,
      Vat_number: formData.vatNumber,
      company_address: selectedCompanyAddress || null,
      is_active: formData.isActive,
      selected_city: formData.type === 'ADMIN' ? '' : formData.selected_city,
      apply_vat: isCustomerType ? formData.applyVat : false,
      apply_delivery_charges: isCustomerType ? formData.applyDeliveryCharges : false
    };
  };

  // Helper function to determine redirect destination after save
  const getRedirectDestination = (newUserId = null) => {
    if (returnTo === 'customers') {
      return selectedShop ? `/shop/${selectedShop}/customers` : '/customers';
    }
    if (returnTo === 'users') {
      return selectedShop ? `/shop/${selectedShop}/users` : '/users';
    }

    const isCustomer = formData.type === 'CUSTOMER' || formData.type === 'INACTIVE_CUSTOMER';

    if (isCustomer) {
      return selectedShop ? `/shop/${selectedShop}/customers` : '/customers';
    } else {
      return selectedShop ? `/shop/${selectedShop}/users` : '/users';
    }
  };

  // ✅ Helper function to extract readable error message from server response
  const getErrorMessage = (err) => {
    if (!err.response?.data) return 'Failed to create user';

    const data = err.response.data;

    if (typeof data === 'string') return data;

    if (data.error) return data.error;
    if (data.message) return data.message;
    if (data.detail) return data.detail;

    // Agar DRF validation errors hain (object format)
    if (typeof data === 'object') {
      const messages = [];
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          messages.push(`${key}: ${value.join(', ')}`);
        } else if (typeof value === 'string') {
          messages.push(`${key}: ${value}`);
        } else if (typeof value === 'object') {
          messages.push(`${key}: ${JSON.stringify(value)}`);
        }
      });
      if (messages.length > 0) return messages.join('\n');
    }

    return 'Failed to create user';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const userData = buildUserData();

    try {
      const response = await axiosInstance.post('/user/users/', userData);

      if (response.status === 201) {
        const shopName = shops.find(s => s.shop_id === parseInt(selectedShop))?.shop_name;
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `${formData.type} ${formData.firstName} ${formData.lastName} added successfully to ${shopName || 'No Shop'}!`,
          timer: 2500,
          showConfirmButton: true
        });

        navigate(getRedirectDestination());
      }
    } catch (err) {
      console.error('Error creating user:', err);

      // ✅ Behtar error handling - server ka exact message show karein
      const errorMsg = getErrorMessage(err);

      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
        timer: 4000,
        showConfirmButton: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndAddAnother = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const userData = buildUserData();

    try {
      await axiosInstance.post('/user/users/', userData);

      const shopName = shops.find(s => s.shop_id === parseInt(selectedShop))?.shop_name;
      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: `${formData.type} added successfully to ${shopName || 'No Shop'}! You can add another user.`,
        timer: 2500,
        showConfirmButton: true
      });

      setFormData({
        password: '',
        type: presetType,
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        companyName: '',
        companySize: '',
        vatNumber: '',
        isActive: true,
        selected_city: '',
        applyVat: false,
        applyDeliveryCharges: false
      });
      setSelectedCompanyAddress('');
      setUserAddress({ house_no: '', street: '', area: '', postcode: '' });

    } catch (err) {
      console.error('Error creating user:', err);
      const errorMsg = getErrorMessage(err);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
        timer: 4000,
        showConfirmButton: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndContinueEditing = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const userData = buildUserData();

    try {
      const response = await axiosInstance.post('/user/users/', userData);

      const shopName = shops.find(s => s.shop_id === parseInt(selectedShop))?.shop_name;
      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: `${formData.type} ${formData.firstName} ${formData.lastName} saved to ${shopName || 'No Shop'}! You can continue editing.`,
        timer: 2500,
        showConfirmButton: true
      });

      if (response.data && response.data.id) {
        if (returnTo === 'customers' || formData.type === 'CUSTOMER' || formData.type === 'INACTIVE_CUSTOMER') {
          if (selectedShop) {
            navigate(`/shop/${selectedShop}/edit-user/${response.data.id}?returnTo=customers`);
          } else {
            navigate(`/edit-user/${response.data.id}?returnTo=customers`);
          }
        } else {
          if (selectedShop) {
            navigate(`/shop/${selectedShop}/edit-user/${response.data.id}`);
          } else {
            navigate(`/edit-user/${response.data.id}`);
          }
        }
      }

    } catch (err) {
      console.error('Error creating user:', err);
      const errorMsg = getErrorMessage(err);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
        timer: 4000,
        showConfirmButton: true
      });
    } finally {
      setLoading(false);
    }
  };

  const currentShop = shops.find(s => s.shop_id === parseInt(selectedShop));

  const getUserTypeIcon = (type) => {
    switch(type) {
      case 'ADMIN':
        return <FaShieldAlt />;
      case 'STAFF':
        return <FaUsers />;
      case 'CUSTOMER':
        return <FaUser />;
      case 'INACTIVE_CUSTOMER':
        return <FaUserSlash />;
      default:
        return <FaUser />;
    }
  };

  const getBackDestination = () => {
    if (returnTo === 'customers') {
      return selectedShop ? `/shop/${selectedShop}/customers` : '/customers';
    }
    if (returnTo === 'users') {
      return selectedShop ? `/shop/${selectedShop}/users` : '/users';
    }
    const isCustomer = formData.type === 'CUSTOMER' || formData.type === 'INACTIVE_CUSTOMER';
    if (isCustomer) {
      return selectedShop ? `/shop/${selectedShop}/customers` : '/customers';
    }
    return selectedShop ? `/shop/${selectedShop}/users` : '/users';
  };

  return (
    <div className="add-user-page">
      {/* Header with back button */}
      <div className="add-user-header">
        <div className="header-left">
          <div className="header-title">
            <h1>Add New User</h1>
          </div>
        </div>
        <button className="back-button" onClick={() => navigate(getBackDestination())}>
          Back to {returnTo === 'customers' || formData.type === 'CUSTOMER' || formData.type === 'INACTIVE_CUSTOMER' ? 'Customers' : 'Users'}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="add-user-form-full">
        {/* Two Column Layout */}
        <div className="form-two-columns">

          {/* Left Column */}
          <div className="form-column">

            {/* Account Information */}
            <div className="form-section">
              <h3>
                <FaLock className="section-icon" />
                Account Information
              </h3>

              <div className="form-group">
                <label>Password: <span className="required">*</span></label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter password (min. 8 characters)"
                  required
                />
              </div>

              <div className="form-group">
                <label>User Type: <span className="required">*</span></label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  {userTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p className="field-hint">
                  {formData.type === 'STAFF' && 'Staff have limited permissions based on assigned roles and city'}
                  {formData.type === 'CUSTOMER' && 'Customer has access to shop services and can make purchases'}
                </p>
              </div>

              {isCustomerType && (
                <div className="billing-flags-group">
                  <label className="billing-flags-title">
                    <FaReceipt className="section-icon" />
                    Billing
                  </label>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="applyVat"
                        checked={formData.applyVat}
                        onChange={handleInputChange}
                      />
                      <span className="checkbox-label"><FaReceipt /> Apply VAT</span>
                    </label>
                    <p className="field-hint">
                      If checked, VAT will be added to this customer's order total when they order from their shop.
                    </p>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="applyDeliveryCharges"
                        checked={formData.applyDeliveryCharges}
                        onChange={handleInputChange}
                      />
                      <span className="checkbox-label"><FaTruck /> Apply Delivery Charges</span>
                    </label>
                    <p className="field-hint">
                      If checked, delivery charges will be added to this customer's order total when they order from their shop.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Shop Selection */}
            <div className="form-section">
              <h3>
                <FaStore className="section-icon" />
                Shop Assignment
              </h3>

              <div className="form-group">
                <label>Select Shop: <span className="required">*</span></label>
                {loadingShops ? (
                  <div className="loading-select">
                    <FaSpinner className="spinner" /> Loading shops...
                  </div>
                ) : (
                  <select
                    value={selectedShop}
                    onChange={handleShopChange}
                    className="form-select"
                    required
                    disabled={!!initialShopId}
                  >
                    <option value="">Select a shop</option>
                    {shops.map(shop => (
                      <option key={shop.shop_id} value={shop.shop_id}>
                        {shop.shop_name} ({shop.subdomain}.{shop.domain})
                      </option>
                    ))}
                  </select>
                )}
                {initialShopId && currentShop && (
                  <p className="field-hint info-hint">
                    <FaStore /> Shop is fixed: {currentShop.shop_name}
                  </p>
                )}
              </div>
            </div>

            {/* CITY SELECTION SECTION */}
            <div className="form-section">
              <h3>
                <FaMapMarkerAlt className="section-icon" />
                City Selection
                {formData.type === 'STAFF' && <span className="required-badge">Required</span>}
                {formData.type === 'CUSTOMER' && <span className="info-hint"> (Optional)</span>}
              </h3>

              <div className="form-group">
                <label>
                  Select City:
                  {formData.type === 'STAFF' && <span className="required">*</span>}
                  {formData.type === 'CUSTOMER' && <span className="info-hint"> (Optional)</span>}
                </label>

                {loadingCities ? (
                  <div className="loading-select">
                    <FaSpinner className="spinner" /> Loading cities...
                  </div>
                ) : (
                  <select
                    name="selected_city"
                    value={formData.selected_city || ''}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={!selectedShop || cities.length === 0 || !!cityFetchError}
                  >
                    <option value="">-- Select a city --</option>
                    {cities.map((city, index) => (
                      <option key={index} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                )}

                {!selectedShop && formData.type !== 'ADMIN' && (
                  <p className="field-hint warning-hint">
                    <FaStore /> Please select a shop first to see available cities
                  </p>
                )}

                {selectedShop && cityFetchError && formData.type !== 'ADMIN' && (
                  <p className="field-hint error-hint">
                    <FaCheckCircle style={{color: 'red'}} /> {cityFetchError}
                  </p>
                )}

                {selectedShop && cities.length === 0 && !loadingCities && !cityFetchError && formData.type !== 'ADMIN' && (
                  <p className="field-hint warning-hint">
                    <FaMapMarkerAlt /> No cities available for this shop.
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => navigate(`/shop/${selectedShop}/edit`)}
                    >
                      Add cities in shop settings
                    </button>
                  </p>
                )}
              </div>
            </div>

            {/* Company Address Section */}
            <div className="form-section">
              <h3>
                <FaAddressCard className="section-icon" />
                Company Address
              </h3>

              <div className="form-group">
                <label>Select Company Address (Optional):</label>
                {loadingAddresses ? (
                  <div className="loading-select">
                    <FaSpinner className="spinner" /> Loading addresses...
                  </div>
                ) : (
                  <select
                    value={selectedCompanyAddress}
                    onChange={(e) => setSelectedCompanyAddress(e.target.value)}
                    className="form-select"
                    disabled={!selectedShop}
                  >
                    <option value="">-- Select a company address --</option>
                    {companyAddresses.map(addr => (
                      <option key={addr.address_Id} value={addr.address_Id}>
                        {addr.city}, {addr.area} - {addr.postal_code}
                        {addr.is_default && ' ✓ (Default)'}
                      </option>
                    ))}
                  </select>
                )}

                {!selectedShop && (
                  <p className="field-hint warning-hint">
                    <FaStore /> Please select a shop first to see company addresses
                  </p>
                )}

                {selectedShop && companyAddresses.length === 0 && !loadingAddresses && (
                  <p className="field-hint warning-hint">
                    <FaMapMarkerAlt /> No company addresses found for {currentShop?.shop_name}.
                  </p>
                )}

                {selectedShop && companyAddresses.length > 0 && !loadingAddresses && (
                  <p className="field-hint success-hint">
                    <FaCheckCircle /> {companyAddresses.length} company address(es) available
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="form-column">

            {/* Basic Information */}
            <div className="form-section">
              <h3>
                <FaUserTag className="section-icon" />
                Basic Information
              </h3>

              <div className="form-row">
                <div className="form-group half">
                  <label>First Name: <span className="required">*</span></label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-group half">
                  <label>Last Name: <span className="required">*</span></label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email: <span className="required">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="user@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number:</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="Enter Phone Number"
                />
              </div>

              {/* ✅ USER ADDRESS FORM - Direct fields for permanent address */}
              <div className="form-group">
                <label>Address (Permanent):</label>

                <div className="user-address-form">
                  <div className="form-row">
                    <div className="form-group half">
                      <label>House No:</label>
                      <input
                        type="text"
                        name="house_no"
                        value={userAddress.house_no}
                        onChange={handleUserAddressChange}
                        placeholder="House / Building number"
                      />
                    </div>
                    <div className="form-group half">
                      <label>Street:</label>
                      <input
                        type="text"
                        name="street"
                        value={userAddress.street}
                        onChange={handleUserAddressChange}
                        placeholder="Street name"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group half">
                      <label>Area:</label>
                      <input
                        type="text"
                        name="area"
                        value={userAddress.area}
                        onChange={handleUserAddressChange}
                        placeholder="Area"
                      />
                    </div>
                    <div className="form-group half">
                      <label>Postcode:</label>
                      <input
                        type="text"
                        name="postcode"
                        value={userAddress.postcode}
                        onChange={handleUserAddressChange}
                        placeholder="Postal / ZIP code"
                      />
                    </div>
                  </div>

                  {/* Live preview of combined address */}
                  {buildFullAddress() && (
                    <div className="selected-address-preview">
                      <strong>Full Address Preview:</strong>
                      <p>{buildFullAddress()}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="form-section">
              <h3>
                <FaBuilding className="section-icon" />
                Company Information
              </h3>

              <div className="form-group">
                <label>Company Name:</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="Company name"
                />
              </div>

              <div className="form-group">
                <label>Company Size:</label>
                <select
                  name="companySize"
                  value={formData.companySize}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  {companySizes.map(size => (
                    <option key={size.value} value={size.value}>
                      {size.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>VAT Number:</label>
                <input
                  type="text"
                  name="vatNumber"
                  value={formData.vatNumber}
                  onChange={handleInputChange}
                  placeholder="VAT number"
                />
              </div>
            </div>

            {/* Status Options */}
            <div className="form-section">
              <h3>Status</h3>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <span className="checkbox-label">Active Account</span>
                </label>
                <p className="field-hint">Inactive users cannot login to the system</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions-full">
          <button type="submit" className="btn-save" disabled={loading}>
            {loading ? <FaSpinner className="spinner" /> : null}
            {loading ? 'SAVING...' : 'SAVE'}
          </button>
          <button type="button" className="btn-save-continue" onClick={handleSaveAndContinueEditing} disabled={loading}>
            Save and continue editing
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddUser;